import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Fully controlled radio that:
 * - passes name/value/checked to the real input
 * - calls onChange(value) when the label (or icon) is clicked
 * - uses a visually-hidden input (NOT display:none) so label clicks work
 */
const InputRadio = React.forwardRef(
  (
    {
      id,
      name,
      value,
      checked,
      onChange,
      disabled,
      labelText,
      labelClassName,
      groupClassName,
      inputClassName,
      iconClassName,
      ...rest
    },
    ref
  ) => {
    const handleLabelClick = (e) => {
      // if already selected, do nothing; otherwise, set the value
      if (disabled) return;
      if (!checked) onChange?.(value);
    };

    return (
      <label
        htmlFor={id}
        className={cn('db-field-radio cursor-pointer select-none', groupClassName, disabled && 'opacity-60')}
        onClick={handleLabelClick}
      >
        <div className={cn('custom-radio inline-flex items-center', inputClassName)}>
          <input
            ref={ref}
            id={id}
            name={name}
            type="radio"
            value={value}
            checked={!!checked}
            onChange={(e) => onChange?.(e.target.value)}
            disabled={disabled}
            className={cn('custom-radio-field')}
            // ensure "visually hidden" not display:none so label clicks still toggle
            style={{
              position: 'absolute',
              opacity: 0,
              width: 0,
              height: 0,
              pointerEvents: 'none',
            }}
            {...rest}
          />
          <span
            className={cn(
              'custom-radio-span inline-flex h-5 w-5 items-center justify-center rounded-full border',
              checked ? 'border-primary ring-2 ring-primary' : 'border-slate-300'
            )}
            role="radio"
            aria-checked={checked ? 'true' : 'false'}
            aria-disabled={disabled ? 'true' : 'false'}
          />
        </div>
        {labelText && <span className={cn('db-field-label ml-2', labelClassName)}>{labelText}</span>}
      </label>
    );
  }
);

InputRadio.displayName = 'InputRadio';
export default InputRadio;
